import { useMemo, useState } from 'react';
import { Combobox } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { banks, Bank } from '@/data/banks';
import { BANCO_ITAU_CODIGO } from '../../../models/candidato.opcoes';
import { INPUT_CLASS } from '../candidatoFormExternoEtapa2Page.definitions';

// A conta pedida é sempre de outro banco — Itaú fica fora da lista.
const bancosOutros = banks.filter((b) => b.code !== BANCO_ITAU_CODIGO);

interface BancoComboboxProps {
  /** Código do banco (ex.: "001") — mesmo valor gravado no perfil (personalAccount.bankCode). */
  codigo: string | null | undefined;
  onChange: (codigo: string) => void;
}

/** Mesma lista de bancos do perfil (BankAccountFieldSet), sem o Itaú, com busca por nome ou código. */
export default function BancoCombobox({ codigo, onChange }: BancoComboboxProps) {
  const [busca, setBusca] = useState('');
  const selecionado = bancosOutros.find((b) => b.code === codigo) ?? null;

  const filtrados = useMemo(() => {
    const termo = busca.toLowerCase().replace(/\s+/g, '');
    if (!termo) return bancosOutros;
    return bancosOutros.filter((b) => b.name.toLowerCase().replace(/\s+/g, '').includes(termo) || b.code.includes(termo));
  }, [busca]);

  return (
    <Combobox value={selecionado} onChange={(banco: Bank | null) => onChange(banco?.code ?? '')}>
      <div className="relative">
        <Combobox.Input
          className={INPUT_CLASS}
          onChange={(e) => setBusca(e.target.value)}
          displayValue={(banco: Bank | null) => (banco ? `${banco.code} - ${banco.name}` : '')}
          placeholder="Digite o nome ou o código do banco"
          autoComplete="off"
        />
        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center px-3">
          <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
        </Combobox.Button>
        {filtrados.length > 0 && (
          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            {filtrados.map((banco) => (
              <Combobox.Option
                key={banco.code}
                value={banco}
                className={({ active }) =>
                  `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                }
              >
                {({ active, selected }) => (
                  <>
                    <span className={`block truncate ${selected ? 'font-semibold' : ''}`}>
                      {banco.code} - {banco.name}
                    </span>
                    {selected && (
                      <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-indigo-600'}`}>
                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                      </span>
                    )}
                  </>
                )}
              </Combobox.Option>
            ))}
          </Combobox.Options>
        )}
      </div>
    </Combobox>
  );
}
